// DispatchDeck — Time Off "group by employee" (TASK-10121)
// ============================================================================
// `window.TimeOffGroup` — the ONE grouping rule for a list of time-off
// requests, shared by HR -> Time Off (views/hr.js renderTimeOffList) and
// Clock -> Time Off (views/clock.js renderTimeOffRequestsList). Lacey (TITS
// office, 2026-10-26): "Time off tickets still arent getting sorted and
// orginized by employee" — both lists now default to this grouping.
//
// The rule (moved VERBATIM out of hr.js renderTimeOffList's TASK-9535 block):
//   * one block per employee, keyed by personId, else employeeId, else the
//     stored name ("name:<name>") for legacy name-only rows;
//   * blocks in name order (localeCompare);
//   * inside a block, pending requests first, then everything else, each part
//     keeping the caller's incoming order (the caller sorts first).
//
// PURE: no DOM, no state, no Firestore. It groups exactly the rows it is given
// and never adds one — a self-scoped Clock viewer (clock-only, no workforce)
// only ever has their own requests in ClockState.timeoffRequests, so grouping
// cannot widen what they see.
//
// LOADING (personComp.js precedent — CLAUDE.md Pitfall #1): imported ONCE via a
// side-effect import from views/clock.js (in app.js's static import graph, so
// it evaluates at startup); hr.js reads `window.TimeOffGroup` at call time,
// NEVER a second `?v=` import. vm-tested by tests/task10121-timeoff-group.unit.cjs.
(function (root) {
  'use strict';

  function storedName(req) {
    return String((req && (req.personName || req.employeeName)) || 'Unknown');
  }

  /** Stable grouping key: the person id when stored, else the stored name. */
  function personKey(req) {
    if (!req) return 'name:Unknown';
    return req.personId || req.employeeId || ('name:' + storedName(req));
  }

  /**
   * @param {Array} rows   requests, already sorted the way each block should read
   * @param {Object} [opts]
   * @param {Function} [opts.nameOf]  req -> display name (Clock resolves the
   *        roster name); defaults to the stored personName / employeeName.
   * @returns {Array<{key, name, personId, items, pendingCount}>}
   */
  function byEmployee(rows, opts) {
    const nameOf = (opts && typeof opts.nameOf === 'function') ? opts.nameOf : storedName;
    const groups = new Map();
    (rows || []).forEach(req => {
      if (!req) return;
      const key = personKey(req);
      if (!groups.has(key)) {
        groups.set(key, { key, name: String(nameOf(req) || storedName(req)), personId: req.personId || req.employeeId || '', items: [] });
      }
      groups.get(key).items.push(req);
    });
    const ordered = Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
    ordered.forEach(g => {
      const pend = g.items.filter(r => r.status === 'pending');
      g.items = pend.concat(g.items.filter(r => r.status !== 'pending'));
      g.pendingCount = pend.length;
    });
    return ordered;
  }

  root.TimeOffGroup = Object.freeze({ personKey, byEmployee });
})(typeof window !== 'undefined' ? window : globalThis);
